import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { StatCard, Button, Badge } from "@/lib/ui";

export default function SellerDashboard() {
  const nav = useNavigate();
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/seller/dashboard").then(({ data }) => setD(data)).catch(() => {});
  }, []);

  if (!d) return <p className="text-slate-400">Loading…</p>;
  const { stats, slots, seller } = d;
  const sub = seller.subscription || {};
  const isPaid = slots.subscribed;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-head text-2xl font-extrabold text-slate-900">Welcome, {seller.company_name || "Seller"}</h1>
          <p className="mt-1 text-sm text-slate-500">Here's how your dead stock is performing.</p>
        </div>
        <Button data-testid="dash-add-product" onClick={() => nav("/seller/app/products/new")}>
          <i className="fa-solid fa-plus" /> Add Product
        </Button>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-7">
        <StatCard testid="stat-sold-value" label="Sold Till Date" value={`₹${Math.round(stats.total_sold_value || 0).toLocaleString("en-IN")}`} icon="fa-indian-rupee-sign" tone="green" />
        <StatCard testid="stat-active" label="Active" value={stats.active} icon="fa-circle-check" tone="green" />
        <StatCard testid="stat-pending" label="Pending" value={stats.pending} icon="fa-clock" tone="amber" />
        <StatCard testid="stat-rejected" label="Rejected" value={stats.rejected} icon="fa-ban" tone="red" />
        <StatCard testid="stat-total" label="Listings" value={stats.total} icon="fa-box" />
        <StatCard testid="stat-views" label="Views" value={stats.views} icon="fa-eye" tone="blue" />
        <StatCard testid="stat-clicks" label="WhatsApp" value={stats.whatsapp_clicks} icon="fa-whatsapp" tone="green" />
      </div>

      <div className="mt-6">
        {isPaid ? (() => {
          const pct = slots.total ? Math.min(100, Math.round((slots.used / slots.total) * 100)) : 0;
          const left = Math.max(slots.total - slots.used, 0);
          const nearly = slots.total > 0 && (left <= 2 || pct >= 80);
          const bar = pct >= 100 ? "bg-red-500" : nearly ? "bg-amber-500" : "bg-emerald-500";
          return (
            <div className={`rounded-2xl border p-5 ${nearly ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-white"}`} data-testid="slot-usage-card">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="font-head text-lg font-bold text-slate-900">Product Slots</h3>
                  <p className="mt-1 text-sm text-slate-600" data-testid="slot-usage-text">Used <b className="text-slate-900">{slots.used}</b> of <b className="text-slate-900">{slots.total}</b> slots · <span className={left === 0 ? "font-semibold text-red-600" : nearly ? "font-semibold text-amber-700" : ""}>{left} left</span></p>
                </div>
                {nearly && <Button onClick={() => nav("/seller/app/subscription")} data-testid="slot-addon-btn"><i className="fa-solid fa-plus" /> Add On a Package</Button>}
              </div>
              <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-slate-200/70"><div className={`h-full rounded-full ${bar} transition-all`} style={{ width: `${pct}%` }} data-testid="slot-usage-bar" /></div>
              <p className="mt-2 text-xs text-slate-500">
                {left === 0 ? "You've used every slot. Add on any package to keep listing — new slots are added to your total instantly on approval." :
                  nearly ? `Only ${left} slot${left === 1 ? "" : "s"} left on your ${sub.plan} plan. Add on a package before you run out.` :
                    <>Your <b>{sub.plan}</b> plan includes {slots.total} product slots, valid for 1 year.</>}
              </p>
            </div>
          );
        })() : (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-orange-200 bg-orange-50 p-5" data-testid="no-plan-card">
            <div>
              <h3 className="font-head text-lg font-bold text-slate-900">No active package</h3>
              <p className="mt-1 text-sm text-slate-600">Choose an annual package to start listing your stock. Start small and add on packages as you grow.</p>
            </div>
            <Button onClick={() => nav("/seller/app/subscription")} data-testid="dash-subscribe-btn"><i className="fa-solid fa-crown" /> View Packages</Button>
          </div>
        )}
      </div>

      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="font-head text-lg font-bold text-slate-900">Approval Target</h3>
        <p className="mt-1 text-sm text-slate-600">
          <i className="fa-solid fa-bolt mr-1 text-orange-500" />
          Submitted products are reviewed and <b>approved within a 48-hour target</b>. You'll see them go live in the marketplace once approved.
        </p>
      </div>
    </div>
  );
}
